import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Scale, 
  MapPin, 
  Mail, 
  Phone,
  Plus,
  Search,
  CheckCircle,
  MessageSquare,
  Send
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const regions = [
  "Auvergne-Rhône-Alpes", "Bourgogne-Franche-Comté", "Bretagne", "Centre-Val de Loire",
  "Corse", "Grand Est", "Hauts-de-France", "Île-de-France", "Normandie",
  "Nouvelle-Aquitaine", "Occitanie", "Pays de la Loire", "Provence-Alpes-Côte d'Azur"
];

const specialties = [
  { id: "urbanisme", label: "Droit de l'urbanisme" },
  { id: "penal", label: "Droit pénal" },
  { id: "habitat_leger", label: "Habitat léger" },
  { id: "environnement", label: "Droit de l'environnement" },
  { id: "administratif", label: "Droit administratif" }
];

export default function Lawyers() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [lawyers, setLawyers] = useState([]);
  const [filteredLawyers, setFilteredLawyers] = useState([]);
  const [regionFilter, setRegionFilter] = useState('all');
  const [specialtyFilter, setSpecialtyFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [showRegisterDialog, setShowRegisterDialog] = useState(false);
  const [showContactDialog, setShowContactDialog] = useState(false);
  const [selectedLawyer, setSelectedLawyer] = useState(null);

  const [newLawyer, setNewLawyer] = useState({
    name: '',
    email: '',
    phone: '',
    region: '',
    specialties: [],
    description: ''
  });

  const [contactForm, setContactForm] = useState({
    subject: '',
    message: '',
    contact_email: user?.email || '',
    contact_phone: ''
  });

  useEffect(() => {
    fetchLawyers();
  }, []);

  useEffect(() => {
    let filtered = [...lawyers];
    
    if (regionFilter !== 'all') {
      filtered = filtered.filter(l => l.region === regionFilter);
    }
    if (specialtyFilter !== 'all') {
      filtered = filtered.filter(l => l.specialties.includes(specialtyFilter));
    }
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(l => 
        l.name.toLowerCase().includes(query) || 
        l.region.toLowerCase().includes(query)
      );
    }
    
    setFilteredLawyers(filtered);
  }, [lawyers, regionFilter, specialtyFilter, searchQuery]);

  const fetchLawyers = async () => {
    try {
      const response = await axios.get(`${API}/lawyers`);
      setLawyers(response.data);
      setFilteredLawyers(response.data);
    } catch (error) {
      console.error('Failed to fetch lawyers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRegisterLawyer = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/lawyers`, newLawyer);
      toast.success('Demande d\'inscription envoyée ! Nous vérifierons vos informations.');
      setShowRegisterDialog(false);
      setNewLawyer({
        name: '',
        email: '',
        phone: '',
        region: '',
        specialties: [],
        description: ''
      });
    } catch (error) {
      console.error('Failed to register lawyer:', error);
      toast.error('Erreur lors de l\'inscription');
    }
  };

  const handleContactLawyer = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.error('Vous devez être connecté pour contacter un avocat');
      return;
    }
    
    try {
      // If lawyer has a user account, send via instant messaging
      if (selectedLawyer.user_id) {
        const token = localStorage.getItem('token');
        // Create conversation with initial message
        await axios.post(`${API}/conversations`, {
          recipient_id: selectedLawyer.user_id,
          initial_message: `[${contactForm.subject}]\n\n${contactForm.message}\n\n---\nContact: ${contactForm.contact_email}${contactForm.contact_phone ? ` | ${contactForm.contact_phone}` : ''}`
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Message envoyé ! Consultez votre messagerie pour la suite de l\'échange.');
        navigate('/messages');
      } else {
        // Fallback to email-based contact for lawyers without user account
        await axios.post(`${API}/lawyers/${selectedLawyer.id}/messages`, {
          ...contactForm,
          lawyer_id: selectedLawyer.id
        });
        toast.success('Message envoyé ! L\'avocat vous répondra par email.');
      }
      
      setShowContactDialog(false);
      setContactForm({
        subject: '',
        message: '',
        contact_email: user?.email || '',
        contact_phone: ''
      });
    } catch (error) {
      console.error('Failed to send message:', error);
      toast.error('Erreur lors de l\'envoi du message');
    }
  };

  const openContactDialog = (lawyer) => {
    setSelectedLawyer(lawyer);
    setContactForm({
      ...contactForm,
      contact_email: user?.email || ''
    });
    setShowContactDialog(true);
  };

  const toggleSpecialty = (specialty) => {
    setNewLawyer(prev => ({
      ...prev,
      specialties: prev.specialties.includes(specialty)
        ? prev.specialties.filter(s => s !== specialty)
        : [...prev.specialties, specialty]
    }));
  };

  const getSpecialtyLabel = (id) => {
    const spec = specialties.find(s => s.id === id);
    return spec ? spec.label : id;
  };

  return (
    <div className="min-h-screen" data-testid="lawyers-page">
      {/* Header */}
      <div className="bg-muted/30 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-2">
            Annuaire des avocats
          </h1>
          <p className="text-muted-foreground">
            Trouvez et contactez un avocat spécialisé dans les questions d'habitat léger.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Info Banner */}
        <Card className="mb-8 bg-primary/5 border-primary/20">
          <CardContent className="py-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                <MessageSquare className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h3 className="font-semibold">Contactez directement les avocats</h3>
                <p className="text-sm text-muted-foreground">
                  Envoyez un message aux avocats spécialisés pour obtenir une première consultation.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Filters */}
        <Card className="mb-8">
          <CardContent className="py-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Rechercher..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                  data-testid="search-input"
                />
              </div>
              <Select value={regionFilter} onValueChange={setRegionFilter}>
                <SelectTrigger data-testid="region-filter">
                  <SelectValue placeholder="Région" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes les régions</SelectItem>
                  {regions.map(region => (
                    <SelectItem key={region} value={region}>{region}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={specialtyFilter} onValueChange={setSpecialtyFilter}>
                <SelectTrigger data-testid="specialty-filter">
                  <SelectValue placeholder="Spécialité" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes les spécialités</SelectItem>
                  {specialties.map(spec => (
                    <SelectItem key={spec.id} value={spec.id}>{spec.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Dialog open={showRegisterDialog} onOpenChange={setShowRegisterDialog}>
                <DialogTrigger asChild>
                  <Button className="gap-2" data-testid="register-lawyer-btn">
                    <Plus className="w-4 h-4" />
                    S'inscrire comme avocat
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg">
                  <DialogHeader>
                    <DialogTitle>S'inscrire comme avocat partenaire</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleRegisterLawyer} className="space-y-4">
                    <div>
                      <Label htmlFor="lawyer-name">Nom complet *</Label>
                      <Input
                        id="lawyer-name"
                        value={newLawyer.name}
                        onChange={(e) => setNewLawyer({ ...newLawyer, name: e.target.value })}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="lawyer-email">Email professionnel *</Label>
                      <Input
                        id="lawyer-email"
                        type="email"
                        value={newLawyer.email}
                        onChange={(e) => setNewLawyer({ ...newLawyer, email: e.target.value })}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="lawyer-phone">Téléphone</Label>
                      <Input
                        id="lawyer-phone"
                        type="tel"
                        value={newLawyer.phone}
                        onChange={(e) => setNewLawyer({ ...newLawyer, phone: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label>Région *</Label>
                      <Select 
                        value={newLawyer.region} 
                        onValueChange={(v) => setNewLawyer({ ...newLawyer, region: v })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Sélectionnez une région" />
                        </SelectTrigger>
                        <SelectContent>
                          {regions.map(region => (
                            <SelectItem key={region} value={region}>{region}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Spécialités *</Label>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {specialties.map(spec => (
                          <Badge
                            key={spec.id}
                            variant={newLawyer.specialties.includes(spec.id) ? 'default' : 'outline'}
                            className="cursor-pointer"
                            onClick={() => toggleSpecialty(spec.id)}
                          >
                            {spec.label}
                          </Badge>
                        ))}
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="lawyer-description">Présentation</Label>
                      <Textarea
                        id="lawyer-description"
                        value={newLawyer.description}
                        onChange={(e) => setNewLawyer({ ...newLawyer, description: e.target.value })}
                        placeholder="Décrivez votre expérience avec l'habitat léger..."
                      />
                    </div>
                    <Button 
                      type="submit" 
                      className="w-full"
                      disabled={!newLawyer.name || !newLawyer.email || !newLawyer.region || newLawyer.specialties.length === 0}
                    >
                      Envoyer ma demande
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </CardContent>
        </Card>

        {/* Results */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="animate-pulse">
                <CardHeader>
                  <div className="h-5 bg-muted rounded w-3/4" />
                </CardHeader>
                <CardContent>
                  <div className="h-20 bg-muted rounded" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : filteredLawyers.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Scale className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">Aucun avocat trouvé</h3>
              <p className="text-muted-foreground mb-4">
                Aucun avocat ne correspond à vos critères de recherche.
              </p>
              <Button variant="outline" onClick={() => {
                setRegionFilter('all');
                setSpecialtyFilter('all');
                setSearchQuery('');
              }}>
                Réinitialiser les filtres
              </Button>
            </CardContent>
          </Card>
        ) : (
          <>
            <p className="text-sm text-muted-foreground mb-4">
              {filteredLawyers.length} avocat{filteredLawyers.length > 1 ? 's' : ''} trouvé{filteredLawyers.length > 1 ? 's' : ''}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredLawyers.map((lawyer) => (
                <Card key={lawyer.id} className="hover:shadow-lg transition-shadow" data-testid={`lawyer-card-${lawyer.id}`}>
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                          <Scale className="w-6 h-6 text-primary" />
                        </div>
                        <div>
                          <CardTitle className="text-lg">{lawyer.name}</CardTitle>
                          <div className="flex items-center gap-1 text-sm text-muted-foreground">
                            <MapPin className="w-3 h-3" />
                            {lawyer.region}
                          </div>
                        </div>
                      </div>
                      {lawyer.verified && (
                        <Badge variant="secondary" className="gap-1">
                          <CheckCircle className="w-3 h-3" />
                          Vérifié
                        </Badge>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-1 mb-4">
                      {lawyer.specialties.map((spec) => (
                        <Badge key={spec} variant="outline" className="text-xs">
                          {getSpecialtyLabel(spec)}
                        </Badge>
                      ))}
                    </div>
                    {lawyer.description && (
                      <CardDescription className="mb-4 line-clamp-2">
                        {lawyer.description}
                      </CardDescription>
                    )}
                    
                    {/* Contact Button - Main CTA */}
                    <Button 
                      className="w-full gap-2 mb-3"
                      onClick={() => {
                        if (!user) {
                          toast.error('Vous devez être connecté pour contacter un avocat');
                          navigate('/connexion');
                          return;
                        }
                        // If lawyer has a user account, use instant messaging
                        if (lawyer.user_id) {
                          navigate(`/messages?to=${lawyer.user_id}&name=${encodeURIComponent(lawyer.name)}`);
                        } else {
                          // Fallback to contact dialog for lawyers without user account
                          openContactDialog(lawyer);
                        }
                      }}
                      data-testid={`contact-lawyer-${lawyer.id}`}
                    >
                      <MessageSquare className="w-4 h-4" />
                      Contacter
                    </Button>
                    
                    <div className="space-y-2 text-sm">
                      <a 
                        href={`mailto:${lawyer.email}`} 
                        className="flex items-center gap-2 text-muted-foreground hover:text-primary"
                      >
                        <Mail className="w-4 h-4" />
                        {lawyer.email}
                      </a>
                      {lawyer.phone && (
                        <a 
                          href={`tel:${lawyer.phone}`} 
                          className="flex items-center gap-2 text-muted-foreground hover:text-primary"
                        >
                          <Phone className="w-4 h-4" />
                          {lawyer.phone}
                        </a>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </>
        )}

        {/* Contact Dialog */}
        <Dialog open={showContactDialog} onOpenChange={setShowContactDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Contacter {selectedLawyer?.name}</DialogTitle>
            </DialogHeader>
            {!user ? (
              <div className="text-center py-8">
                <p className="text-muted-foreground mb-4">
                  Vous devez être connecté pour contacter un avocat.
                </p>
                <Button asChild>
                  <a href="/connexion">Se connecter</a>
                </Button>
              </div>
            ) : (
              <form onSubmit={handleContactLawyer} className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Envoyez un message à cet avocat. Il vous répondra par email.
                </p>
                <div>
                  <Label htmlFor="contact-subject">Objet *</Label>
                  <Input
                    id="contact-subject"
                    value={contactForm.subject}
                    onChange={(e) => setContactForm({ ...contactForm, subject: e.target.value })}
                    placeholder="Ex: Demande de conseil pour mise en demeure"
                    required
                    data-testid="contact-subject-input"
                  />
                </div>
                <div>
                  <Label htmlFor="contact-message">Message *</Label>
                  <Textarea
                    id="contact-message"
                    value={contactForm.message}
                    onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                    placeholder="Décrivez brièvement votre situation..."
                    rows={5}
                    required
                    data-testid="contact-message-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="contact-email">Votre email *</Label>
                    <Input
                      id="contact-email"
                      type="email"
                      value={contactForm.contact_email}
                      onChange={(e) => setContactForm({ ...contactForm, contact_email: e.target.value })}
                      required
                      data-testid="contact-email-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="contact-phone">Téléphone (optionnel)</Label>
                    <Input
                      id="contact-phone"
                      type="tel"
                      value={contactForm.contact_phone}
                      onChange={(e) => setContactForm({ ...contactForm, contact_phone: e.target.value })}
                      data-testid="contact-phone-input"
                    />
                  </div>
                </div>
                <Button type="submit" className="w-full gap-2" data-testid="send-message-btn">
                  <Send className="w-4 h-4" />
                  Envoyer le message
                </Button>
              </form>
            )}
          </DialogContent>
        </Dialog>

        {/* Info card */}
        <Card className="mt-12">
          <CardHeader>
            <CardTitle>Aide juridictionnelle</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground mb-4">
              Si vos revenus sont modestes, vous pouvez bénéficier de l'aide juridictionnelle 
              qui prend en charge tout ou partie des frais d'avocat.
            </p>
            <a 
              href="https://www.service-public.fr/particuliers/vosdroits/F18074" 
              target="_blank" 
              rel="noopener noreferrer"
            >
              <Button variant="outline">En savoir plus sur l'aide juridictionnelle</Button>
            </a>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
